import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { interviewApi, interviewerApi, userApi, AdminUser, InterviewConfig } from '../../services/api';
import { InterviewerItem } from '../../types';
import { INTERVIEW_TECHNOLOGIES } from '../../content/interviewsPage';
import { useAuth } from '../../context/AuthContext';
import { canManageInterviews } from '../../utils/roles';
import { confirmAction } from '../../utils/confirmAction';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';

const schema = z
  .object({
    technology: z.string().min(1, 'Please select a technology'),
    otherTechnology: z.string().optional(),
    yearsOfExperience: z
      .number({ invalid_type_error: 'Years of experience is required' })
      .min(0, 'Enter a valid number')
      .max(50, 'Enter a valid number'),
    experienceDetails: z.string().min(10, 'Please describe the relevant experience'),
    role: z.string().min(2, 'Current or desired role is required'),
    roleDetails: z.string().min(10, 'Please add role details'),
    linkedInUrl: z
      .string()
      .url('Please enter a valid URL')
      .refine((value) => value.toLowerCase().includes('linkedin.com'), {
        message: 'Please enter a LinkedIn URL',
      }),
    additionalNotes: z.string().optional(),
    fee: z.number({ invalid_type_error: 'Fee is required' }).min(0, 'Fee cannot be negative'),
    meetingLink: z.string().url('Enter a valid meeting URL'),
    scheduledDate: z.string().min(1, 'Scheduled date is required'),
    scheduledTime: z.string().min(1, 'Scheduled time is required'),
  })
  .refine(
    (data) => data.technology !== 'Other' || (data.otherTechnology && data.otherTechnology.trim().length > 1),
    { message: 'Please specify the technology', path: ['otherTechnology'] },
  );

type FormData = z.infer<typeof schema>;

const fieldClasses = `block w-full rounded-md border-2 border-gray-300 shadow-sm bg-white text-gray-900
  placeholder:text-gray-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20
  px-4 py-2.5 text-base`;
const labelClasses = 'block text-sm font-semibold text-gray-900 mb-1.5';

const AdminScheduleInterviewPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<AdminUser[]>([]);
  const [interviewers, setInterviewers] = useState<InterviewerItem[]>([]);
  const [config, setConfig] = useState<InterviewConfig | null>(null);
  const [candidateQuery, setCandidateQuery] = useState('');
  const [interviewerQuery, setInterviewerQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<AdminUser | null>(null);
  const [selectedInterviewer, setSelectedInterviewer] = useState<InterviewerItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      technology: '',
      otherTechnology: '',
      experienceDetails: '',
      role: '',
      roleDetails: '',
      additionalNotes: '',
      meetingLink: '',
      scheduledDate: '',
      scheduledTime: '',
    },
  });

  const technology = watch('technology');
  const technologies = config?.technologies?.length ? config.technologies : [...INTERVIEW_TECHNOLOGIES];

  useEffect(() => {
    userApi.adminListUsers().then((r) => setCandidates(r.data.users || [])).catch(() => undefined);
    interviewerApi.list().then((r) => setInterviewers(r.data.interviewers || [])).catch(() => undefined);
    interviewApi.getConfig().then((r) => {
      const next = r.data as InterviewConfig;
      setConfig(next);
      if (next.payablePrice != null) {
        setValue('fee', Number(next.payablePrice));
      }
    }).catch(() => undefined);
  }, [setValue]);

  const approvedInterviewers = useMemo(
    () => interviewers.filter((person) => !person.approvalStatus || person.approvalStatus === 'Approved'),
    [interviewers],
  );

  const candidateMatches = useMemo(() => {
    const query = candidateQuery.trim().toLowerCase();
    if (!query) return candidates.slice(0, 8);
    return candidates.filter((person) =>
      [person.username, person.email, person.mobile].some((value) => value?.toLowerCase().includes(query)),
    ).slice(0, 8);
  }, [candidates, candidateQuery]);

  const interviewerMatches = useMemo(() => {
    const query = interviewerQuery.trim().toLowerCase();
    const pool = approvedInterviewers;
    if (!query) return pool.slice(0, 8);
    return pool.filter((person) =>
      [person.fullName, person.contactEmail, person.currentJobTitle, ...(person.technologies || [])]
        .some((value) => value?.toLowerCase().includes(query)),
    ).slice(0, 8);
  }, [approvedInterviewers, interviewerQuery]);

  const pickCandidate = (person: AdminUser) => {
    setSelectedCandidate(person);
    setCandidateQuery('');
    if (person.linkedInUrl) setValue('linkedInUrl', person.linkedInUrl);
    if (person.username) setValue('role', person.username);
  };

  const onSubmit = async (data: FormData) => {
    if (!selectedCandidate) {
      setError('Search and select a candidate.');
      return;
    }
    if (!selectedInterviewer) {
      setError('Search and select an interviewer.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await interviewApi.adminCreate({
        userProfileId: selectedCandidate.id,
        interviewerId: selectedInterviewer.interviewerId,
        technology: data.technology,
        otherTechnology: data.otherTechnology,
        yearsOfExperience: data.yearsOfExperience,
        experienceDetails: data.experienceDetails,
        role: data.role,
        roleDetails: data.roleDetails,
        linkedInUrl: data.linkedInUrl,
        additionalNotes: data.additionalNotes,
        price: data.fee,
        scheduledDate: data.scheduledDate,
        scheduledTime: data.scheduledTime,
        meetingLink: data.meetingLink,
      });
      if (!response.data.success || !response.data.interviewRequest) {
        setError(response.data.message || 'Could not schedule the interview.');
        return;
      }
      navigate(`/interviews/${response.data.interviewRequest.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not schedule the interview.');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageInterviews(user)) {
    return <p className="max-w-3xl mx-auto px-4 py-16 text-gray-600">Only Owner or InterviewAdmin can schedule interviews.</p>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Link to="/admin/dashboard?tab=school-interviews" className="text-sm font-medium text-primary-700 hover:text-primary-800">
        ← Interviews
      </Link>
      <h1 className="mt-4 text-3xl font-bold text-gray-900">Schedule interview</h1>
      <p className="mt-2 text-sm text-gray-600">Search the candidate and interviewer, then complete the session details.</p>

      {error && <p className="mt-4 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</p>}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">People</h2>
          <div>
            <label className={labelClasses}>Candidate</label>
            {selectedCandidate ? (
              <div className="flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
                <p className="text-sm text-gray-800">
                  <span className="font-semibold">{selectedCandidate.username || selectedCandidate.email}</span>
                  <span className="text-gray-600"> · {selectedCandidate.email}</span>
                </p>
                <button type="button" className="text-sm font-semibold text-primary-700" onClick={() => setSelectedCandidate(null)}>Change</button>
              </div>
            ) : (
              <>
                <input
                  className={fieldClasses}
                  value={candidateQuery}
                  onChange={(e) => setCandidateQuery(e.target.value)}
                  placeholder="Search name, email, or mobile"
                />
                <ul className="mt-2 divide-y rounded-md border bg-white">
                  {candidateMatches.map((person) => (
                    <li key={person.id}>
                      <button type="button" className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm" onClick={() => pickCandidate(person)}>
                        <span className="font-medium text-gray-900">{person.username || person.email}</span>
                        <span className="text-gray-600"> · {person.email}</span>
                        {person.mobile ? <span className="text-gray-500"> · {person.mobile}</span> : null}
                      </button>
                    </li>
                  ))}
                  {candidateMatches.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">No matching candidates.</li>}
                </ul>
              </>
            )}
          </div>
          <div>
            <label className={labelClasses}>Interviewer</label>
            {selectedInterviewer ? (
              <div className="flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
                <p className="text-sm text-gray-800">
                  <span className="font-semibold">{selectedInterviewer.fullName}</span>
                  <span className="text-gray-600"> · {selectedInterviewer.contactEmail}</span>
                </p>
                <button type="button" className="text-sm font-semibold text-primary-700" onClick={() => setSelectedInterviewer(null)}>Change</button>
              </div>
            ) : (
              <>
                <input
                  className={fieldClasses}
                  value={interviewerQuery}
                  onChange={(e) => setInterviewerQuery(e.target.value)}
                  placeholder="Search name, email, or technology"
                />
                <ul className="mt-2 divide-y rounded-md border bg-white">
                  {interviewerMatches.map((person) => (
                    <li key={person.interviewerId}>
                      <button type="button" className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm" onClick={() => setSelectedInterviewer(person)}>
                        <span className="font-medium text-gray-900">{person.fullName}</span>
                        <span className="text-gray-600"> · {person.contactEmail}</span>
                        {person.technologies?.length ? <span className="text-gray-500"> · {person.technologies.join(', ')}</span> : null}
                      </button>
                    </li>
                  ))}
                  {interviewerMatches.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">No matching interviewers.</li>}
                </ul>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Session details</h2>
          <label className={labelClasses}>Technology
            <select className={`${fieldClasses} mt-1.5`} {...register('technology')}>
              <option value="">Select a technology</option>
              {technologies.map((tech) => <option key={tech} value={tech}>{tech}</option>)}
            </select>
          </label>
          {errors.technology && <p className="text-sm text-error-600">{errors.technology.message}</p>}
          {technology === 'Other' && (
            <Input id="otherTechnology" label="Specify technology" error={errors.otherTechnology?.message} {...register('otherTechnology')} />
          )}
          <Input
            id="yearsOfExperience"
            type="number"
            step="0.5"
            label="Years of experience"
            error={errors.yearsOfExperience?.message}
            {...register('yearsOfExperience', { valueAsNumber: true })}
          />
          <label className={labelClasses}>Relevant experience details
            <textarea className={`${fieldClasses} mt-1.5`} rows={4} {...register('experienceDetails')} />
            {errors.experienceDetails && <p className="mt-1 text-sm text-error-600">{errors.experienceDetails.message}</p>}
          </label>
          <Input id="role" label="Current or desired role" error={errors.role?.message} {...register('role')} />
          <label className={labelClasses}>Role details
            <textarea className={`${fieldClasses} mt-1.5`} rows={4} {...register('roleDetails')} />
            {errors.roleDetails && <p className="mt-1 text-sm text-error-600">{errors.roleDetails.message}</p>}
          </label>
          <Input id="linkedInUrl" label="Candidate LinkedIn URL" error={errors.linkedInUrl?.message} {...register('linkedInUrl')} />
          <label className={labelClasses}>Additional notes
            <textarea className={`${fieldClasses} mt-1.5`} rows={3} {...register('additionalNotes')} />
          </label>
          <Input id="fee" type="number" min={0} step="1" label="Fee" error={errors.fee?.message} {...register('fee', { valueAsNumber: true })} />
          <Input id="meetingLink" type="url" label="Meeting link" error={errors.meetingLink?.message} {...register('meetingLink')} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input id="scheduledDate" type="date" label="Scheduled date" error={errors.scheduledDate?.message} {...register('scheduledDate')} />
            <Input id="scheduledTime" type="time" label="Scheduled time" error={errors.scheduledTime?.message} {...register('scheduledTime')} />
          </div>
        </Card>

        <div className="flex flex-wrap gap-3">
          <Button type="submit" isLoading={saving}>Schedule interview</Button>
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              const ok = await confirmAction({
                title: 'Discard this interview?',
                description: 'The schedule details you entered will not be saved.',
                confirmLabel: 'Discard',
                cancelLabel: 'Keep editing',
                variant: 'danger',
              });
              if (!ok) return;
              navigate('/admin/dashboard?tab=school-interviews');
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminScheduleInterviewPage;
